// Has to be the first import for I.E
import 'regenerator-runtime/runtime';
import { createRoot } from 'react-dom/client';

// eslint-disable-next-line no-use-before-define
import React, { Suspense, useEffect, useState } from 'react';
import { BrowserRouter, Route, Switch } from 'react-router-dom';
import { ThemeContext } from './contexts';
import AppShell from 'op-components/AppShell/AppShell';

import 'shared-components/Styles/Main.scss';

import { SetMomentLocale } from 'shared-components/utils';
import Favicon from 'react-favicon';
import { NavigationContextProvider, ROBase, Refresh, RegistrationContextProvider, region } from 'op-components';
import { isDemo } from 'op-utils';
import { AssessmentProvider } from 'op-pages/OP/PatientFormSummary/context';
import { Region } from 'shared-components/enums';

import {
  AppSelector,
  CovidContainer,
  CreatePatient,
  DTContainer,
  DocumentsPageRouter,
  ErrorPage,
  HAContainer,
  HomeRegistrationLogin,
  HomeRegoEndSession,
  InformationNotice,
  LinkExpired,
  LogoutOP,
  MOBase,
  MOCreatePatient,
  MODashboard,
  MOPatientCarePlanPage,
  MOPatientSummary,
  ManagementPlan,
  NotFoundPage,
  Notes,
  OutcomesPage,
  PSODashboard,
  PatientFormSummaryApollo,
  PatientNavigationApollo,
  PreCtChartCheckApollo,
  PreCtTriageApollo,
  ROCreatePatient,
  ROPatientCarePlanPage,
  ROPatientLabs,
  ROPatientSummaryPage,
  RegistrationAU,
  RegistrationAddressApollo,
  RegistrationBasicApollo,
  RegistrationComplete,
  RegistrationConflictsApollo,
  RegistrationContactDetailsApollo,
  RegistrationFileAttachmentsApollo,
  RegistrationInformationNoticeApollo,
  RegistrationInsuranceApollo,
  RegistrationLoginApollo,
  RegistrationUS,
  SSOPage,
  SessionTimeout,
  Summary,
  UKAppointmentsDashboardApollo,
  UKHomePage,
  UKPatientSearch,
  UKRegistrationDemographicsApollo,
  UKRegistrationGPApollo,
  UKRegistrationSummaryApollo,
  InsightsDashboard,
  ROPatientTracker,
  ROPatientPathway,
  TreatmentReview,
  TreatmentReviewForm,
} from 'op-pages';
import UKRegistrationAltContactApollo from 'op-pages/OP/RegistrationForm/UKRegistrationAltContact/UKRegistrationAltContactApollo';
import SentryUser from 'shared-components/components/SessionData/SentryUser';
import AppVersion from 'shared-components/components/SessionData/AppVersion';
import * as Sentry from '@sentry/react';
import { Logger } from 'shared-components/utils';
import { sentryBeforeSend } from 'shared-components/utils/sentryBeforeSend';
import { SENTRY_DATA_COLLECTION } from 'shared-components/utils/sentryDataCollection';
import { FormContextProvider } from './pages/OP/PatientNavigation/context';
import { ThemeProvider, StyledEngineProvider } from '@mui/material';
import { datadogRum } from '@datadog/browser-rum';
import type { RumInitConfiguration } from '@datadog/browser-rum';
import DatadogUserInfo from 'shared-components/components/DatadogUserInfo/DatadogUserInfo';
import { GCTheme, LumonusTheme } from 'theme';
import Dashboard from 'op-pages/RO/Dashboard/Dashboard';
import OutcomeFormPage from 'op-pages/RO/Outcomes/OutcomeFormPage';
import { ApolloProviderWithError } from 'op-utils/ApolloSetup';
import { ErrorModalContextProvider, useErrorModalContext } from 'op-contexts/ErrorModalContext/ErrorModalContext';
import { ScribeContextProvider } from 'op-contexts';
import { setDayjsLocale } from 'op-utils/dayjs';
import SummaryPage from './pages/RO/AiSummary/SummaryPage';
import ChatPage from 'op-pages/RO/AiSummary/ChatPage';
import ScribePage from './pages/RO/Scribe/ScribePage';
import HealthTimelinePage from 'op-pages/RO/AiSummary/HealthTimelinePage';
import ThemeShowcase from './pages/ThemeShowcase/ThemeShowcase';
import { LicenseInfo } from '@mui/x-license-pro';

LicenseInfo.setLicenseKey(import.meta.env.MUI_X_LICENSE_KEY);

interface DatadogRumConfig extends RumInitConfiguration {
  enabled: boolean;
}

setDayjsLocale();
const SentryRoute = Sentry.withSentryRouting(Route);

const ENVIRONMENT = import.meta.env.ENVIRONMENT || 'unset';
const DEPLOYMENT_CODE = import.meta.env.DEPLOYMENT_CODE || 'unset';

const SENTRY_DSN = import.meta.env.SENTRY_DSN;
if (SENTRY_DSN) {
  Sentry.init({
    dsn: SENTRY_DSN,
    tracesSampleRate: 0.2,
    environment: ENVIRONMENT,
    release: import.meta.env.SENTRY_RELEASE || 'unset',
    beforeSend: sentryBeforeSend,
    dataCollection: SENTRY_DATA_COLLECTION,
  });
  Sentry.getGlobalScope().setTag('deployment_code', DEPLOYMENT_CODE);
}

const logger = new Logger('index');
const defaultTheme = import.meta.env.REACT_APP_THEME === 'Lumonus' ? LumonusTheme : GCTheme;

const initializeDatadogRum = async () => {
  try {
    const configResponse = await fetch(`${window.location.origin}/server/analytics-rum-config`);
    if (!configResponse.ok) {
      logger.debug('DataDog RUM init', 'Config fetch failed:', configResponse.status);
      return;
    }

    const config = (await configResponse.json()) as DatadogRumConfig;
    if (!config.enabled) {
      logger.debug('DataDog RUM init', 'RUM disabled by backend config');
      return;
    }

    // Validate required fields before initializing
    if (!config.applicationId) {
      logger.error('DataDog RUM init', 'applicationId is missing in config');
      return;
    }
    if (!config.clientToken) {
      logger.error('DataDog RUM init', 'clientToken is missing in config');
      return;
    }

    const { enabled, ...rumConfig } = config;
    datadogRum.init(rumConfig);
  } catch (error) {
    logger.error('DataDog RUM init', 'Error initializing RUM:', error);
  }
};

// Initialize RUM; backend config.enabled governs activation
initializeDatadogRum();

// Set the moment locale -- this may need to change when the language is to be settable by the patient/user
SetMomentLocale();

const ManualErrorTrigger = () => {
  useEffect(() => {
    throw new Error('Manual error triggered, please contact support');
  }, []);

  return <div>Manual Error</div>;
};

const routes = (
  <Switch>
    <SentryRoute exact path="/" component={SSOPage} />
    <SentryRoute path="/theme-showcase" component={ThemeShowcase} />
    <SentryRoute path="/homeRegistration/informationNotice" component={InformationNotice} />
    <SentryRoute path="/homeRegistration/linkExpired" component={LinkExpired} />
    <SentryRoute path="/home-registration/validate/:token" component={HomeRegistrationLogin} />
    <SentryRoute path="/patient/:patientId/covid" component={CovidContainer} />
    <SentryRoute path="/patient/:patientId/distress/:distressId" component={DTContainer} />
    <SentryRoute path="/patient/:patientId/health" component={HAContainer} />
    <SentryRoute
      path="/patient/:patientId/registration"
      component={region === Region.US ? RegistrationUS : RegistrationAU}
    />
    <SentryRoute path="/patient/registrationComplete" component={RegistrationComplete} />
    <SentryRoute path="/patient/:patientId/summary" component={PatientFormSummaryApollo} />
    <SentryRoute path="/patient/:patientId/home" component={PatientNavigationApollo} />
    <SentryRoute path="/patient/:patientId/nurse/prectchartcheck/:formId" component={PreCtChartCheckApollo} />
    <SentryRoute path="/patient/:patientId/nurse/precttriage/:formId" component={PreCtTriageApollo} />
    <SentryRoute path="/patient/:patientId/management">
      <AppShell>
        <ManagementPlan />
      </AppShell>
    </SentryRoute>
    <SentryRoute path="/patient/create" component={CreatePatient} />
    <SentryRoute path="/patient" component={RegistrationLoginApollo} />
    {/* UK registration */}
    <SentryRoute path="/registration/:patientId/address" component={RegistrationAddressApollo} />
    <SentryRoute path="/registration/:patientId/altcontact" component={UKRegistrationAltContactApollo} />
    <SentryRoute path="/registration/:patientId/basic" component={RegistrationBasicApollo} />
    <SentryRoute path="/registration/:patientId/contact" component={RegistrationContactDetailsApollo} />
    <SentryRoute path="/registration/:patientId/demographics" component={UKRegistrationDemographicsApollo} />
    <SentryRoute path="/registration/:patientId/gp" component={UKRegistrationGPApollo} />
    <SentryRoute path="/registration/:patientId/infonotice" component={RegistrationInformationNoticeApollo} />
    <SentryRoute path="/registration/:patientId/insurance" component={RegistrationInsuranceApollo} />
    <SentryRoute path="/registration/:patientId/attachments" component={RegistrationFileAttachmentsApollo} />
    <SentryRoute path="/registration/:patientId/summary" component={UKRegistrationSummaryApollo} />
    <SentryRoute path="/registration/:patientId/conflicts" component={RegistrationConflictsApollo} />
    {/* Other */}
    <SentryRoute path="/endSession" component={HomeRegoEndSession} />
    <SentryRoute path="/timeout" component={SessionTimeout} />
    <SentryRoute path="/error" component={ErrorPage} />
    <SentryRoute
      exact
      path="/manual-error"
      component={() => (
        <SentryUser>
          <ManualErrorTrigger />
        </SentryUser>
      )}
    />
    <SentryRoute path="/app-selector" component={AppSelector} />
    <SentryRoute path="/logout" component={LogoutOP} />
    <SentryRoute path="/px">
      <Switch>
        <SentryRoute
          path="/px/*"
          component={() => {
            window.location.href = 'https://patient.genesiscare.com';
            return null;
          }}
        />
      </Switch>
    </SentryRoute>
    <SentryRoute
      path={['/search', '/review-forms', '/sms-logs', '/appointments', '/navigator', '/radiation', '/medonc']}>
      <AppShell>
        <Switch>
          <SentryRoute path="/search" component={region === Region.UK ? UKPatientSearch : PSODashboard} />
          <SentryRoute path="/review-forms" component={PSODashboard} />
          <SentryRoute path="/sms-logs" component={PSODashboard} />
          {region === Region.UK && <SentryRoute path="/appointments" component={UKAppointmentsDashboardApollo} />}
          <SentryRoute path="/navigator">
            <ROBase>
              <Switch>
                <SentryRoute exact path="/navigator/patient/:id/summary" component={ROPatientSummaryPage} />
              </Switch>
            </ROBase>
          </SentryRoute>
          <SentryRoute path="/radiation">
            <ScribeContextProvider>
              <ROBase>
                <Switch>
                  {isDemo && <SentryRoute path="/radiation/patient-tracker/" component={ROPatientTracker} />}
                  {isDemo && <SentryRoute path="/:oncologyType/patient/:id/pathway" component={ROPatientPathway} />}
                  <SentryRoute path="/radiation/insights/" component={InsightsDashboard} />
                  <SentryRoute
                    path="/:oncologyType/patient/:id/careplan/:careplanId/"
                    component={ROPatientCarePlanPage}
                  />
                  <SentryRoute exact path="/:oncologyType/patient/:id/summary/:tab?" component={ROPatientSummaryPage} />
                  <SentryRoute path="/:oncologyType/patient/:id/documents/:page" component={DocumentsPageRouter} />
                  <SentryRoute exact path="/:oncologyType/patient/:id/new" component={Summary} />
                  <SentryRoute path="/:oncologyType/patient/:id/notes" component={Notes} />
                  <SentryRoute path="/:oncologyType/patient/:id/labs" component={ROPatientLabs} />
                  <SentryRoute path="/:oncologyType/patient/:id/ai-summary" component={SummaryPage} />
                  <SentryRoute path="/:oncologyType/patient/:id/health-timeline" component={HealthTimelinePage} />
                  <SentryRoute path="/:oncologyType/patient/:id/chat" component={ChatPage} />
                  <SentryRoute path="/:oncologyType/patient/:id/scribe" component={ScribePage} />
                  <SentryRoute path="/:oncologyType/patient/:id/outcomes/:outcomeId/" component={OutcomeFormPage} />
                  <SentryRoute path="/:oncologyType/patient/:id/outcomes" component={OutcomesPage} />
                  <SentryRoute
                    path="/:oncologyType/patient/:id/treatment-review/:groupId"
                    component={TreatmentReviewForm}
                  />
                  <SentryRoute path="/:oncologyType/patient/:id/treatment-review" component={TreatmentReview} />
                  <SentryRoute path="/:oncologyType/registration/patient/:id" component={ROCreatePatient} />
                  <SentryRoute path="/:oncologyType" component={Dashboard} />
                </Switch>
              </ROBase>
            </ScribeContextProvider>
          </SentryRoute>
          <SentryRoute path="/medonc">
            <MOBase>
              <Switch>
                <SentryRoute
                  path="/:oncologyType/patient/:id/careplan/:careplanId/"
                  component={MOPatientCarePlanPage}
                />
                <SentryRoute exact path="/:oncologyType/patient/:id/summary" component={MOPatientSummary} />
                <SentryRoute path="/:oncologyType/patient/create" component={MOCreatePatient} />
                <SentryRoute path="/:oncologyType" component={MODashboard} />
              </Switch>
            </MOBase>
          </SentryRoute>
        </Switch>
      </AppShell>
    </SentryRoute>
    {/* Catch-all: unauthenticated users are redirected to SSO login, authenticated users see a 404 page */}
    <SentryRoute path="*" component={NotFoundPage} />
  </Switch>
);

const ErrorModalBoundary = (): JSX.Element => {
  const { setError } = useErrorModalContext();
  useEffect(() => {
    setError('Generic');
  }, [setError]);
  return <></>;
};

const WrappedApp = (): JSX.Element => {
  const [theme, setTheme] = useState(defaultTheme);

  return (
    <React.StrictMode>
      <ThemeContext.Provider value={{ theme, setTheme }}>
        <Favicon url={theme?.custom?.favicon} />
        <Suspense fallback={<div />}>
          <ThemeProvider theme={theme}>
            <ErrorModalContextProvider>
              <ApolloProviderWithError>
                <Sentry.ErrorBoundary fallback={<ErrorModalBoundary />}>
                  <NavigationContextProvider>
                    <AssessmentProvider>
                      <RegistrationContextProvider>
                        <Refresh />
                        <BrowserRouter>
                          <AppVersion />
                          <SentryUser />
                          <DatadogUserInfo />
                          <StyledEngineProvider injectFirst>
                            <FormContextProvider>
                              {region === Region.UK && <UKHomePage routes={routes} />}
                              {region !== Region.UK && routes}
                            </FormContextProvider>
                          </StyledEngineProvider>
                        </BrowserRouter>
                      </RegistrationContextProvider>
                    </AssessmentProvider>
                  </NavigationContextProvider>
                </Sentry.ErrorBoundary>
              </ApolloProviderWithError>
            </ErrorModalContextProvider>
          </ThemeProvider>
        </Suspense>
      </ThemeContext.Provider>
    </React.StrictMode>
  );
};

const container = document.getElementById('root');
const root = createRoot(container!);
root.render(<WrappedApp />);
